import { Controller } from "react-hook-form";
import type { Control, FormState, DeepMap, FieldError } from "react-hook-form";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import { stringArrayToOptionArray } from "../../pages/admin/SellerAdmin/PIM/SellerAdminTemplates/AddItemToTemplateCollectionForm";
import { formatMandatoryLabel } from "../../pages/SharedPages/OrganizationPage/ProductsList/NewProductForm/utils";
import type {
  CreatedOption,
  MethodsOfUseForm,
  OptionType,
} from "../../types/types";
import type { AttributeSchema } from "../../types/types.PIM";
import {
  buildListCreatableOptionProps,
  useLazyListBackedChoices,
} from "../../util/attributeChoices";
import { CreatableSelectBox } from "../CreateableSelectBox/CreatableSelectBox";
import { SelectBoxV2 } from "../SelectBoxV2/SelectBoxV2";

type SingleSelectListBackedFieldProps = {
  tenantId: string;
  attribute: AttributeSchema;
  control: Control<Record<string, unknown>>;
  errors: DeepMap<Record<string, unknown>, FieldError>;
  formState: FormState<Record<string, unknown>>;
  isDisabled: boolean;
  isClearable: boolean;
  isAutofilled: boolean;
  showMandatoryAsterisk?: boolean;
  canCreateOptions: boolean;
  onCreateOption?: (value: string, attribute: AttributeSchema) => void;
};

export function SingleSelectListBackedField({
  tenantId,
  attribute,
  control,
  errors,
  formState,
  isDisabled,
  isClearable,
  isAutofilled,
  showMandatoryAsterisk,
  canCreateOptions,
  onCreateOption,
}: SingleSelectListBackedFieldProps): JSX.Element {
  const { t } = useTranslation();
  const {
    choices,
    isLoading,
    onMenuOpen,
    onMenuScrollToBottom,
    onInputChange,
    inputValue,
    filterOption,
  } = useLazyListBackedChoices(tenantId, attribute);
  const options = choices.map(stringArrayToOptionArray);
  const creatableProps = buildListCreatableOptionProps({
    t,
    listName: attribute.list_name,
    optionValues: options.map(({ value }) => value),
    onCreateOption: (value) => onCreateOption?.(value, attribute),
    enabled: canCreateOptions,
  });

  return (
    <Controller
      control={control}
      name={attribute.id}
      defaultValue={
        attribute.values?.length
          ? {
              label: attribute.values[0].enum_variant?.variant,
              value: attribute.values[0].enum_variant?.variant,
            }
          : null
      }
      rules={{
        required: attribute.is_required,
      }}
      render={({ onChange, ...rest }) => (
        <CreatableSelectBox
          {...rest}
          isClearable={isClearable}
          isAutofilled={isAutofilled}
          isLoading={isLoading}
          placeholder={formatMandatoryLabel(
            attribute,
            t,
            showMandatoryAsterisk
          )}
          options={options}
          errors={errors}
          formState={formState}
          disabled={isDisabled}
          onMenuOpen={onMenuOpen}
          onMenuScrollToBottom={onMenuScrollToBottom}
          onInputChange={onInputChange}
          inputValue={inputValue}
          filterOption={filterOption}
          {...creatableProps}
          onChange={(option: CreatedOption | null) => {
            onChange(option, { shouldValidate: true });
          }}
        />
      )}
    />
  );
}

type CollectionSingleSelectListBackedFieldProps = {
  col: AttributeSchema;
  tenantId: string;
  defaultValue: OptionType<string> | { label?: string; value?: string };
  methodsOfUseForm: MethodsOfUseForm;
  errors: DeepMap<Record<string, unknown>, FieldError>;
  t: TFunction;
  is_single_select_disabled: boolean;
  menuPortalTarget?: HTMLElement | null;
  menuPortalZIndex?: number;
  dropdownZ: number;
  isPackagingTypeField: boolean;
  onPackagingTypeChange: (
    selectedValue: OptionType<string> | null,
    onChange: (value: OptionType<string> | null) => void
  ) => void;
};

export function CollectionSingleSelectListBackedField({
  col,
  tenantId,
  defaultValue,
  methodsOfUseForm,
  errors,
  t,
  is_single_select_disabled,
  menuPortalTarget,
  menuPortalZIndex,
  dropdownZ,
  isPackagingTypeField,
  onPackagingTypeChange,
}: CollectionSingleSelectListBackedFieldProps): JSX.Element {
  const { control } = methodsOfUseForm;
  const {
    choices,
    isLoading,
    onMenuOpen,
    onMenuScrollToBottom,
    onInputChange,
    inputValue,
    filterOption,
  } = useLazyListBackedChoices(tenantId, col);

  return (
    <Controller
      control={control}
      name={col.id}
      rules={{
        required: col.is_required,
      }}
      defaultValue={defaultValue as OptionType<string>}
      render={({ onChange, value: formValue, name: fieldName }) => {
        const options = choices.map((choice) => ({
          label: choice,
          value: choice,
        }));

        return (
          <SelectBoxV2
            key={fieldName || undefined}
            name={fieldName}
            placeholder={col.display_name ? t([col.display_name]) : col.name}
            normalPlaceholder={menuPortalZIndex != null}
            menuPortalTarget={menuPortalTarget}
            isClearable
            options={options}
            isLoading={isLoading}
            onMenuOpen={onMenuOpen}
            onMenuScrollToBottom={onMenuScrollToBottom}
            onInputChange={onInputChange}
            inputValue={inputValue}
            filterOption={filterOption}
            value={formValue?.value ? (formValue as OptionType<string>) : null}
            onChange={(selectedValue: OptionType<string> | null) => {
              if (isPackagingTypeField) {
                onPackagingTypeChange(selectedValue, onChange);
              } else {
                onChange(selectedValue);
              }
            }}
            errors={errors}
            formState={{ submitCount: 1 }}
            disabled={
              !col.is_editable ||
              col.is_not_applicable ||
              is_single_select_disabled
            }
            styles={
              menuPortalTarget !== undefined
                ? {
                    menuPortal: (base: Record<string, unknown>) => ({
                      ...base,
                      zIndex: dropdownZ,
                    }),
                  }
                : undefined
            }
          />
        );
      }}
    />
  );
}
